import { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import type { GuestSource, GuestStatus, RestaurantTable } from '@sebastian/shared';
import { GuestMixFields } from './GuestMix';
import { Button, Field, Modal, Textarea } from './ui';

/**
 * Inline SVG rather than lucide: at 22px with a 2px hairline both the glyph and
 * the near-black button under it sank into the canvas. Heavier strokes and a
 * accent ring tie the steppers to the count they drive.
 */
function StepButton({
  label,
  sign,
  onClick,
}: {
  label: string;
  sign: 'minus' | 'plus';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-accent/45 bg-raised text-accent-strong transition hover:border-accent hover:bg-accent/12 active:bg-accent/20"
    >
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.75}
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M5 12h14" />
        {sign === 'plus' ? <path d="M12 5v14" /> : null}
      </svg>
    </button>
  );
}

/**
 * Guest count is mandatory before an order can be created (Business Rule 2),
 * so this dialog is the only way onto a free table.
 */
export function OpenTableDialog({
  table,
  open,
  busy,
  onCancel,
  onConfirm,
}: {
  table: RestaurantTable | null;
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (
    guestCount: number,
    notes: string | null,
    guest: { guestSource: GuestSource | null; guestStatus: GuestStatus | null },
  ) => void;
}) {
  const [guests, setGuests] = useState(2);
  const [notes, setNotes] = useState('');
  const [source, setSource] = useState<GuestSource | null>(null);
  const [status, setStatus] = useState<GuestStatus | null>(null);

  // Start from the table's own capacity — usually the right answer.
  useEffect(() => {
    if (open && table) {
      setGuests(Math.min(table.capacity, 2) || 2);
      setNotes('');
      setSource(null);
      setStatus(null);
    }
  }, [open, table]);

  if (!table) return null;

  const quick = [1, 2, 3, 4, 5, 6, 8, 10].filter((count) => count <= Math.max(table.capacity, 10));

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={`Open Table ${table.number}`}
      subtitle={`${table.name}${table.section ? ` · ${table.section}` : ''} · seats ${table.capacity}`}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            loading={busy}
            onClick={() =>
              onConfirm(guests, notes.trim() || null, { guestSource: source, guestStatus: status })
            }
          >
            Start Table
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div>
          <span className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-faint">
            <Users size={14} /> How many guests?
          </span>

          <div className="flex items-center justify-center gap-6 rounded-xl border border-line bg-canvas py-6">
            <StepButton
              label="One fewer guest"
              sign="minus"
              onClick={() => setGuests((count) => Math.max(1, count - 1))}
            />
            <div className="tnum w-24 text-center font-display text-6xl leading-none text-accent-strong">
              {guests}
            </div>
            <StepButton
              label="One more guest"
              sign="plus"
              onClick={() => setGuests((count) => Math.min(100, count + 1))}
            />
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {quick.map((count) => (
              <button
                key={count}
                type="button"
                onClick={() => setGuests(count)}
                className={
                  'h-11 min-w-11 rounded-lg border px-3 text-sm font-semibold transition ' +
                  (guests === count
                    ? 'border-accent bg-accent/15 text-accent-strong'
                    : 'border-line text-ink-soft hover:border-line-strong hover:text-ink')
                }
              >
                {count}
              </button>
            ))}
          </div>
          {guests > table.capacity ? (
            <p className="mt-2 text-xs text-caution">
              That is above this table's stated capacity of {table.capacity}.
            </p>
          ) : null}
        </div>

        <GuestMixFields
          source={source}
          status={status}
          onSourceChange={setSource}
          onStatusChange={setStatus}
        />

        <Field label="Table note (optional)" hint="Allergies, occasion, seating preference…">
          <Textarea
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Birthday — dessert with a candle"
          />
        </Field>
      </div>
    </Modal>
  );
}
